import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import MainSidebar from "@/shared/MainSidebar";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../@/components/ui/tabs";
import { AddPathwayDialog } from "../forms/addPathway";
import AddRoleDialog from "@/forms/addRoles";
import AdminDataTable from "../components/adminDataTable";
import AddUserDialog from "@/forms/addUser";
import { Input } from "../../@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { MoreVertical, Search } from "lucide-react";
import { Badge } from "../../@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../@/components/ui/dropdown-menu";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { EmptyState } from "../components/emptyState";
import { BookOpenIcon } from "lucide-react";
import { Spinner } from "../components/spinner";
import { useAuth } from "@clerk/clerk-react";
import EditItemDialog from "@/forms/editAdminTable";
import DeleteItemDialog from "@/forms/deleteItem";
import { toast } from "sonner";
import { Button } from "../../@/components/ui/button";
import AssignRoleDialog from "@/forms/assignRole";
import AssignRolesBulkDialog from "@/forms/assignRolesBulk";
import TableToolbar from "../components/tableToolbar";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import EditUserRolesDialog from "@/forms/editUserRoles";
import EditUserRolesBulkDialog from "@/forms/editUserRolesBulk";
import EditRoleDialog from "@/forms/editRole";
import BatchDeleteRoles from "@/forms/batchDeleteRoles";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/apiRequest";

export default function AdminPortal() {
  const location = useLocation();
  const navigate = useNavigate();
  const urlTab =
    typeof window !== "undefined"
      ? new URLSearchParams(location.search).get("tab")
      : null;
  const initialTab =
    (location.state && location.state.tab) || urlTab || "users";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [filterByRole, setFilterByRole] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editType, setEditType] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [assignRoleOpen, setAssignRoleOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignRolesBulkOpen, setAssignRolesBulkOpen] = useState(false);
  const [bulkSelectedUsers, setBulkSelectedUsers] = useState([]);
  const [usersSelectionResetKey, setUsersSelectionResetKey] = useState(0);
  const [editUserRolesBulkOpen, setEditUserRolesBulkOpen] = useState(false);
  const [bulkUsersForRoleEdit, setBulkUsersForRoleEdit] = useState([]);
  const [editUserRolesOpen, setEditUserRolesOpen] = useState(false);
  const [userForRoleEdit, setUserForRoleEdit] = useState(null);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [roleForEdit, setRoleForEdit] = useState(null);
  const [rolesBulkDeleteOpen, setRolesBulkDeleteOpen] = useState(false);
  const [bulkRolesForDelete, setBulkRolesForDelete] = useState([]);
  const [rolesSelectionResetKey, setRolesSelectionResetKey] = useState(0);
  const [pendingFilterByRole, setPendingFilterByRole] = useState(filterByRole);
  const [pendingSortBy, setPendingSortBy] = useState(sortBy);
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const formatLabel = (value, fallback = "Unknown") => {
    if (value == null) return fallback;
    const upper = String(value).trim().toUpperCase();
    if (!upper) return fallback;
    return upper
      .replace(/[_-]+/g, " ")
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase());
  };

  useEffect(() => {
    setPendingFilterByRole(filterByRole);
  }, [filterByRole]);
  useEffect(() => {
    setPendingSortBy(sortBy);
  }, [sortBy]);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (activeTab === "users") fetchUsers();
    if (activeTab === "roles") fetchRoles();
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const token = await getToken();
      if (!token) return;
      // Fetch all pages (backend caps responses to e.g. 200 users)
      const [users, usersWithRoles] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ["admin", "users"],
          queryFn: () =>
            fetchAllPaginatedItems({
              url: api_endpoint + "/user/users",
              token,
              selectItems: (data) => data?.users ?? [],
              mode: "skip",
              pageSize: 200,
            }),
        }),
        queryClient.fetchQuery({
          queryKey: ["admin", "users-with-roles"],
          queryFn: () =>
            fetchAllPaginatedItems({
              url: api_endpoint + "/user/users-with-roles",
              token,
              selectItems: (data) => data?.users ?? [],
              mode: "skip",
              pageSize: 200,
              // Preserve multiple roles per user; dedupe by user-role pair.
              getItemKey: (row) => `${row?.user_id ?? ""}-${row?.role_id ?? ""}`,
            }),
        }),
      ]);

      console.log("Users Fetched:", users);
      console.log("Users with Roles Fetched:", usersWithRoles);
      // Create a map of user_id to role information for quick lookup
      const roleMap = new Map();
      usersWithRoles.forEach((userRole) => {
        if (!roleMap.has(userRole.user_id)) {
          roleMap.set(userRole.user_id, []);
        }
        roleMap.get(userRole.user_id).push({
          roleId: userRole.role_id,
          roleTitle: userRole.role_title,
          userRoleCreatedAt: userRole.user_role_created_at,
        });
      });
      // Combine users data with role information
      const formatted = users.map((user) => {
        const roleInfo = roleMap.get(user.id);

        return {
          id: user.id || "Not available",
          name:
            (user.first_name && user.last_name
              ? `${user.first_name} ${user.last_name}`
              : user.first_name || user.last_name) || "Not available",
          role: roleInfo, // Use role from users-with-roles or default
          roleId: roleInfo || null,
          email: user.email || "Not available",
          username: user.username || "Not available",
          lastLogin: user.last_login_at || "Not available",
          avatarUrl: user.avatar_url || null,
          createdAt: user.created_at || "Not available",
          userRoleCreatedAt: roleInfo?.userRoleCreatedAt || null,
        };
      });
      setUsers(formatted);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setError("Failed to load users. Please try again.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshUsers = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    await queryClient.invalidateQueries({
      queryKey: ["admin", "users-with-roles"],
    });
    await fetchUsers();
  };

  const handleUserRolesUpdated = async (payload) => {
    const userId = payload?.userId;
    const nextRoles = payload?.assignedRoles;

    if (userId && Array.isArray(nextRoles)) {
      setUsers((prev) =>
        (prev || []).map((row) => {
          if (String(row?.id) !== String(userId)) return row;
          return {
            ...row,
            role: nextRoles.map((r) => ({
              roleId: r?.roleId,
              roleTitle: r?.roleTitle,
            })),
          };
        })
      );
    }

    await refreshUsers();
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await queryClient.fetchQuery({
        queryKey: ["admin", "roles"],
        queryFn: ({ signal }) =>
          apiRequest(getToken, {
            path: "/user/roles",
            auth: "required",
            signal,
          }),
      });
      console.log("Roles Fetched:", res);

      const fetched = res?.roles || [];
      const formatted = fetched.map((item) => ({
        id: item.id || "Not available",
        role: item.title || "Not available",
        description: item.description || "Not available",
        userCount: item.user_count ?? "Not available",
      }));
      setRoles(formatted);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      setError("Failed to load roles. Please try again.");
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshRoles = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "roles"] });
    await fetchRoles();
  };

  // Dynamic role filter options
  const roleFilterOptions = useMemo(
    () => [
      { value: "ALL", label: "All Roles" },
      ...roles.map((r) => ({
        value: r.role,
        label: r.role,
      })),
    ],
    [roles]
  );

  // Sort options
  const userSortOptions = [
    { value: "username", label: "Username" },
    { value: "name", label: "Name" },
    { value: "email", label: "Email" },
    { value: "role", label: "Role" },
  ];

  const roleSortOptions = [
    { value: "role", label: "Role" },
    { value: "description", label: "Description" },
    { value: "userCount", label: "User Count" },
  ];

  // Filtering and sorting for users
  const filteredSortedUsers = useMemo(() => {
    let result = users;
    // Filter by role
    if (filterByRole !== "ALL") {
      result = result.filter(
        (user) =>
          Array.isArray(user.role) &&
          user.role.some((r) => r.roleTitle === filterByRole)
      );
    }
    // Search
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (user) =>
          user.name.toLowerCase().includes(search) ||
          user.email.toLowerCase().includes(search) ||
          user.username.toLowerCase().includes(search)
      );
    }
    // Sort
    if (sortBy) {
      result = [...result].sort((a, b) => {
        if (sortBy === "name")
          return (a.name || "").localeCompare(b.name || "");
        if (sortBy === "email")
          return (a.email || "").localeCompare(b.email || "");
        if (sortBy === "username")
          return (a.username || "").localeCompare(b.username || "");
        if (sortBy === "role") {
          const aRole =
            Array.isArray(a.role) && a.role.length > 0
              ? a.role[0].roleTitle
              : "";
          const bRole =
            Array.isArray(b.role) && b.role.length > 0
              ? b.role[0].roleTitle
              : "";
          return (aRole || "").localeCompare(bRole || "");
        }
        return 0;
      });
    }
    return result;
  }, [users, filterByRole, searchTerm, sortBy]);

  // Filtering and sorting for roles
  const filteredSortedRoles = useMemo(() => {
    let result = roles;
    // Search
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (role) =>
          role.role.toLowerCase().includes(search) ||
          role.description.toLowerCase().includes(search)
      );
    }
    // Sort
    if (sortBy) {
      result = [...result].sort((a, b) => {
        if (sortBy === "role")
          return (a.role || "").localeCompare(b.role || "");
        if (sortBy === "description")
          return (a.description || "").localeCompare(b.description || "");
        if (sortBy === "userCount")
          return (a.userCount || 0) - (b.userCount || 0);
        return 0;
      });
    }
    return result;
  }, [roles, searchTerm, sortBy]);

  const userColumns = [
    { key: "username", label: "Username" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    {
      key: "role",
      label: "Role(s)",
      render: (value) => {
        if (Array.isArray(value) && value.length > 0) {
          const roleColors = {
            ADMIN: "bg-(--neutral-900) text-(--neutral-50)",
            INSTRUCTOR: "bg-(--color-accent) text-white",
            STUDENT: "bg-(--color-primary) text-white",
          };

          return (
            <div className="flex flex-wrap gap-1">
              {value.map((role, idx) => (
                <Badge
                  key={role.roleId || idx}
                  className={`rounded-full text-[12px] px-2 py-1 font-body border-none ${
                    roleColors[role.roleTitle?.toUpperCase()] ||
                    "bg-(--color-primary) text-white"
                  }`}
                >
                  {formatLabel(role.roleTitle, "Student")}
                </Badge>
              ))}
            </div>
          );
        }

        return (
          <Badge className="rounded-full text-[12px] px-2 py-1 font-body bg-(--color-primary) text-white border-none">
            {formatLabel("STUDENT", "Student")}
          </Badge>
        );
      },
    },
    // { key: 'lastLogin', label: 'Last Login' },
    {
      key: "more",
      label: "",
      render: (_value, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions">
              <MoreVertical className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="font-body"
              onClick={() => {
                setSelectedUser(row);
                setAssignRoleOpen(true);
              }}
            >
              Assign Role
            </DropdownMenuItem>
            <DropdownMenuItem
              className="font-body"
              onClick={() => {
                setUserForRoleEdit(row);
                setEditUserRolesOpen(true);
              }}
            >
              Edit Role
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-(--color-destructive) font-body"
              onClick={() => {
                handleDelete(row, "users");
              }}
            >
              Delete user
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const roleColumns = [
    { key: "role", label: "Role" },
    { key: "description", label: "Description" },
    { key: "userCount", label: "User Count" },
    {
      key: "more",
      label: "",
      render: (_value, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions">
              <MoreVertical className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="font-body"
              onClick={() => {
                setRoleForEdit(row);
                setEditRoleOpen(true);
              }}
            >
              Edit role
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-(--color-destructive) font-body"
              onClick={() => {
                handleDelete(row, "roles");
              }}
            >
              Delete role
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const userFields = [
    { key: "username", label: "Username" },
    { key: "name", label: "Name", required: true },
    { key: "role", label: "Role", required: true },
    { key: "email", label: "Email", required: true },
    { key: "lastLogin", label: "Last Login" }
  ];
  const roleFields = [
    { key: "role", label: "Role", required: true },
    { key: "description", label: "Description", required: true },
  ];

  const handleEdit = (item, type) => {
    setEditItem(item);
    setEditType(type);
    setEditDialogOpen(true);
  };

  const handleDelete = (item, type = null) => {
    setDeleteItem(item);
    setDeleteType(type || activeTab); // track what kind of resource user is deleting
    setDeleteDialogOpen(true);
  };

  const handleAssignUsers = (role) => {
    console.log("Assign users to role:", role);
  };

  const handleAssignRole = (user) => {
    console.log("Assign role to user:", user);
  };

  const handleAddPathway = (pathway) => {
    console.log("Add pathway:", pathway);
  };

  const handleApplyFilters = () => {
    setSearchTerm(searchTerm);
    setFilterByRole(pendingFilterByRole);
  };
  const handleApplySort = () => {
    setSortBy(pendingSortBy);
  };

  const handleAddUser = async (user) => {
    try {
      setLoading(true);
      setError("");

      const token = await getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      // POST request to add a user
      const response = await axios.post(
        api_endpoint + "/users",
        user,
        reqConfig
      );

      console.log("User added:", response.data);

      // refresh user list after adding
      fetchUsers();
    } catch (err) {
      console.error("Failed to add user:", err);
      setError("Failed to add user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = (role) => {
    console.log("Add role:", role);
  };

  const handleSaveEdit = async (form) => {
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      if (editType === "role") {
        const payload = {
          title: form.role,
          description: form.description,
        };
        await axios.put(
          api_endpoint + `/user/roles/edit/${form.id}`,
          payload,
          reqConfig
        );
        toast.success("Role updated successfully");
        setEditDialogOpen(false);
        fetchRoles();
      } else if (editType === "user") {
        const payload = {
          name: form.name,
          email: form.email,
          username: form.username,
          role_ids: Array.isArray(form.role)
            ? form.role.map((r) => Number(r.roleId))
            : [],
        };
        await axios.put(
          api_endpoint + `/user/users/${form.id}`,
          payload,
          reqConfig
        );
        toast.success("User updated successfully");
        setEditDialogOpen(false);
        fetchUsers();
      }
    } catch (error) {
      console.error("Failed to save edits:", error);
      toast.error("Failed to save changes. Please try again.");
    }
  };

  const handleConfirmDelete = async (item) => {
    setDeleteLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      let response;
      if (deleteType === "roles") {
        response = await axios.delete(
          `${api_endpoint}/user/roles/${item.id}`,
          reqConfig
        );
      } else if (deleteType === "users") {
        response = await axios.delete(
          `${api_endpoint}/user/delete-user/${item.id}`,
          reqConfig
        );
      }

      console.log("Delete response:", response.data);
      toast.success(`Item deleted successfully`);
      setDeleteDialogOpen(false);
      if (deleteType === "roles") {
        fetchRoles();
      }
      if (deleteType === "users") {
        fetchUsers();
      }
    } catch (error) {
      console.error("Failed to delete item:", error);
      toast.error("Failed to delete item. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUsersBulkAction = (label, ctx) => {
    const action = String(label ?? "").trim().toLowerCase();
    const rows = Array.isArray(ctx?.selectedRowsOnPage)
      ? ctx.selectedRowsOnPage
      : [];
    if (rows.length < 2) return;

    if (action === "assign role") {
      setBulkSelectedUsers(rows);
      setAssignRolesBulkOpen(true);
      return;
    }

    if (action === "edit roles") {
      setBulkUsersForRoleEdit(rows);
      setEditUserRolesBulkOpen(true);
    }
  };

  const handleRolesBulkAction = (label, ctx) => {
    const action = String(label ?? "").trim().toLowerCase();
    const rows = Array.isArray(ctx?.selectedRowsOnPage)
      ? ctx.selectedRowsOnPage
      : [];

    if (rows.length < 2) return;

    if (action.includes("delete")) {
      setBulkRolesForDelete(rows);
      setRolesBulkDeleteOpen(true);
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen">
      <h1>User Management</h1>
      <p className="text-(--neutral-800)">
        Manage users, user roles, and the modular access polices for each user
        role
      </p>
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value);
          try {
            const params = new URLSearchParams(location.search);
            params.set("tab", value);
            navigate({
              to: `${location.pathname}?${params.toString()}`,
              replace: true,
            });
          } catch (e) {
            console.warn("Failed to update tab in URL", e);
          }
        }}
        className="space-y-4 mt-3"
      >
        <div className="flex flex-col w-full gap-2 sm:flex-col md:flex-row md:items-center md:justify-between">
          <TabsList className="tabs-list w-full md:w-auto">
            <TabsTrigger
              value="users"
              className="tab-trigger w-full md:w-auto px-3 py-0.5 text-sm"
            >
              Users
            </TabsTrigger>
            <TabsTrigger
              value="roles"
              className="tab-trigger w-full md:w-auto px-3 py-0.5 text-sm"
            >
              Roles
            </TabsTrigger>
          </TabsList>
          {/* {activeTab === "users" && <AddUserDialog onAdd={fetchUsers} />} */}
          {activeTab === "roles" && (
            <AddRoleDialog onAdd={fetchRoles} refetchRoles={fetchRoles} />
          )}
        </div>

        <TabsContent value="users" className="space-y-4">
          {error ? (
            <EmptyState icon={BookOpenIcon} title="Error" description={error} />
          ) : loading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <>
              <TableToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filters={[
                  {
                    label: "Filter by Role",
                    value: pendingFilterByRole,
                    onChange: setPendingFilterByRole,
                    onClear: () => setPendingFilterByRole("ALL"),
                    options: roleFilterOptions,
                  },
                ]}
                sorts={[
                  {
                    label: "Sort by",
                    value: pendingSortBy,
                    onChange: setPendingSortBy,
                    onClear: () => setPendingSortBy("name"),
                    options: userSortOptions,
                  },
                ]}
                onApplyFilters={handleApplyFilters}
                onApplySort={handleApplySort}
              />
              <AdminDataTable
                columns={userColumns}
                data={filteredSortedUsers}
                // onEdit={item => handleEdit(item, "user")}
                // onDelete={handleDelete}
                onAssignRole={(user) => {
                  setSelectedUser(user);
                  setAssignRoleOpen(true);
                }}
                enableBulkEditRoles
                onBulkAction={handleUsersBulkAction}
                headerClassName="text-sm font-heading font-semibold text-(--neutral-900)"
                loading={loading}
                selectionResetKey={usersSelectionResetKey}
              />
            </>
          )}
        </TabsContent>
        <TabsContent value="roles" className="space-y-4">
          {error ? (
            <EmptyState icon={BookOpenIcon} title="Error" description={error} />
          ) : loading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <>
              <TableToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filters={[]} // No filter for roles
                sorts={[
                  {
                    label: "Sort by",
                    value: pendingSortBy,
                    onChange: setPendingSortBy,
                    onClear: () => setPendingSortBy("role"),
                    options: roleSortOptions,
                  },
                ]}
                onApplyFilters={handleApplyFilters}
                onApplySort={handleApplySort}
              />
              <AdminDataTable
                columns={roleColumns}
                data={filteredSortedRoles}
                onAssignUsers={null}
                bulkActions={["Delete Roles"]}
                onBulkAction={handleRolesBulkAction}
                headerClassName="text-sm font-heading font-semibold text-(--neutral-900)"
                loading={loading}
                selectionResetKey={rolesSelectionResetKey}
              />
            </>
          )}
        </TabsContent>
      </Tabs>
      <EditItemDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        item={editItem}
        fields={
          editType === "role"
            ? [
                { key: "role", label: "Role", required: true },
                { key: "description", label: "Description", required: true },
              ]
            : editType === "user"
            ? [
                { key: "username", label: "Username" },
                { key: "name", label: "Name", required: true },
                { key: "role", label: "Role", required: true },
                { key: "email", label: "Email", required: true },
              ]
            : []
        }
        onSave={handleSaveEdit}
        loading={loading}
      />
      <DeleteItemDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        item={deleteItem}
        itemLabel={deleteType}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
      <AssignRoleDialog
        user={selectedUser}
        open={assignRoleOpen}
        onOpenChange={setAssignRoleOpen}
        onAssigned={fetchUsers}
      />

      <AssignRolesBulkDialog
        users={bulkSelectedUsers}
        open={assignRolesBulkOpen}
        onOpenChange={(next) => {
          setAssignRolesBulkOpen(next);
          if (!next) setBulkSelectedUsers([]);
        }}
        onAssigned={async () => {
          await refreshUsers();
          setUsersSelectionResetKey((k) => k + 1);
        }}
      />

      <EditUserRolesBulkDialog
        users={bulkUsersForRoleEdit}
        open={editUserRolesBulkOpen}
        onOpenChange={(next) => {
          setEditUserRolesBulkOpen(next);
          if (!next) setBulkUsersForRoleEdit([]);
        }}
        onUpdated={async () => {
          await refreshUsers();
          setUsersSelectionResetKey((k) => k + 1);
        }}
      />
      <EditUserRolesDialog
        user={userForRoleEdit}
        open={editUserRolesOpen}
        onOpenChange={(next) => {
          setEditUserRolesOpen(next);
          if (!next) setUserForRoleEdit(null);
        }}
        onUpdated={handleUserRolesUpdated}
      />
      <EditRoleDialog
        role={roleForEdit}
        open={editRoleOpen}
        onOpenChange={(next) => {
          setEditRoleOpen(next);
          if (!next) setRoleForEdit(null);
        }}
        onUpdated={fetchRoles}
      />

      <BatchDeleteRoles
        open={rolesBulkDeleteOpen}
        onOpenChange={(next) => {
          setRolesBulkDeleteOpen(next);
          if (!next) setBulkRolesForDelete([]);
        }}
        selectedRoles={bulkRolesForDelete}
        refetchRoles={refreshRoles}
        onDeleted={() => {
          setRolesBulkDeleteOpen(false);
          setBulkRolesForDelete([]);
          setRolesSelectionResetKey((k) => k + 1);
        }}
      />
    </div>
  );
}
